import { Site } from "@inrange/building-manager-api-client/models-site";
import { Modal, ModalView } from "@inrange/theme-components";
import { useMemo, useState } from "react";
import { Button, Form, Table } from "react-bootstrap";
import { STATUS_COLOR, STATUS_SYMBOL } from "../../utils/scoreFactorStyles";

type ScoreChangeRow = {
  kind: "score-change";
  siteId: string;
  siteName: string;
  currentScore: number | null | undefined;
  suggestedScore: number;
  factors: NonNullable<Site["suggestedScoreFactors"]>;
};

type FactorRefreshRow = {
  kind: "factor-refresh";
  siteId: string;
  siteName: string;
  currentScore: number;
  suggestedScore: number;
  factors: NonNullable<Site["suggestedScoreFactors"]>;
};

type SuggestionRow = ScoreChangeRow | FactorRefreshRow;

const BulkAutomateScoring = ({
  sites,
  selectedSiteIds,
  onSitesUpdate,
  onClose,
}: {
  sites: Site[];
  selectedSiteIds: Set<string>;
  onSitesUpdate: (
    payload: { [key: string]: any },
    options?: { actionLabel?: string }
  ) => void;
  onClose: () => void;
}) => {
  const rows: SuggestionRow[] = useMemo(() => {
    const candidates: SuggestionRow[] = [];
    for (const site of sites) {
      if (!selectedSiteIds.has(site.id)) continue;
      const suggested = site.suggestedScore;
      if (suggested === null || suggested === undefined) continue;
      const current = site.score ?? null;
      if (suggested !== current) {
        candidates.push({
          kind: "score-change",
          siteId: site.id,
          siteName: site.name,
          currentScore: current,
          suggestedScore: suggested,
          factors: site.suggestedScoreFactors ?? [],
        });
        continue;
      }
      // Score already matches the suggestion. Include the row only when the
      // persisted factors are missing (manually-set legacy score with no
      // tooltip data) so the bulk action can backfill them.
      const persistedFactors = site.scoreFactors ?? [];
      if (current !== null && persistedFactors.length === 0) {
        candidates.push({
          kind: "factor-refresh",
          siteId: site.id,
          siteName: site.name,
          currentScore: current,
          suggestedScore: suggested,
          factors: site.suggestedScoreFactors ?? [],
        });
      }
    }
    candidates.sort((a, b) => a.siteName.localeCompare(b.siteName));
    return candidates;
  }, [sites, selectedSiteIds]);

  const [checkedSiteIds, setCheckedSiteIds] = useState<Set<string>>(
    () => new Set()
  );

  const toggleChecked = (siteId: string) => {
    setCheckedSiteIds((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) {
        next.delete(siteId);
      } else {
        next.add(siteId);
      }
      return next;
    });
  };

  const allChecked = rows.length > 0 && checkedSiteIds.size === rows.length;
  const someChecked = checkedSiteIds.size > 0 && !allChecked;
  const headerCheckboxRef = (el: HTMLInputElement | null) => {
    if (el) el.indeterminate = someChecked;
  };
  const toggleAll = () => {
    if (allChecked) {
      setCheckedSiteIds(new Set());
    } else {
      setCheckedSiteIds(new Set(rows.map((r) => r.siteId)));
    }
  };

  const buildPayloadForRow = (row: SuggestionRow): { [key: string]: any } => {
    if (row.kind === "score-change") {
      return {
        site: { score: row.suggestedScore, scoreFactors: row.factors },
        userNote: "Bulk auto-scoring",
      };
    }
    return {
      site: { scoreFactors: row.factors },
      userNote: "Bulk auto-scoring (refresh factors)",
    };
  };

  const submitUpdates = (siteIdsToUpdate: string[]) => {
    if (siteIdsToUpdate.length === 0) return;
    const updates = siteIdsToUpdate.reduce(
      (acc, siteId) => {
        const row = rows.find((r) => r.siteId === siteId);
        if (!row) return acc;
        acc[siteId] = buildPayloadForRow(row);
        return acc;
      },
      {} as { [key: string]: any }
    );
    onSitesUpdate(updates, { actionLabel: "score" });
  };

  const checkedCount = checkedSiteIds.size;

  return (
    <Modal>
      <ModalView
        id="bulk-automate-scoring"
        title={`Automate scoring for ${selectedSiteIds.size} site${selectedSiteIds.size === 1 ? "" : "s"}`}
        fontWeight="500"
        fontSize="20px"
        width="90vw"
        maxHeight="100%"
        titlePadding="0 0 10px 0"
      >
        {rows.length === 0 ? (
          <>
            <p style={{ marginTop: "10px" }}>
              No score updates needed. Every selected site either already
              matches its suggested score with up-to-date reasoning, or does not
              have enough modelled data to be scored.
            </p>
            <div
              style={{
                marginTop: "16px",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button variant="inrangesecondary" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <>
            <p style={{ marginTop: "10px", marginBottom: "8px" }}>
              {rows.length} of {selectedSiteIds.size} selected site
              {selectedSiteIds.size === 1 ? "" : "s"} would be updated. Tick the
              ones to update, or use &quot;Update all scores&quot; to apply
              every suggestion below.
            </p>
            <div style={{ overflowY: "auto", maxHeight: "60vh" }}>
              <Table
                striped
                bordered
                style={{ fontSize: "14px", marginTop: "10px" }}
              >
                <thead
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "white",
                    zIndex: 1,
                  }}
                >
                  <tr>
                    <th style={{ width: "40px" }}>
                      <Form.Check
                        type="checkbox"
                        ref={headerCheckboxRef}
                        checked={allChecked}
                        onChange={toggleAll}
                        aria-label={
                          allChecked ? "Deselect all sites" : "Select all sites"
                        }
                      />
                    </th>
                    <th>Site name</th>
                    <th style={{ width: "100px" }}>Current score</th>
                    <th style={{ width: "110px" }}>Suggested score</th>
                    <th>Reasoning</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.siteId}>
                      <td style={{ verticalAlign: "top" }}>
                        <Form.Check
                          type="checkbox"
                          checked={checkedSiteIds.has(row.siteId)}
                          onChange={() => toggleChecked(row.siteId)}
                          aria-label={`Select ${row.siteName}`}
                        />
                      </td>
                      <td style={{ verticalAlign: "top" }}>{row.siteName}</td>
                      <td style={{ verticalAlign: "top" }}>
                        {row.kind === "score-change" &&
                        (row.currentScore === null ||
                          row.currentScore === undefined)
                          ? "No score"
                          : row.currentScore}
                      </td>
                      <td style={{ verticalAlign: "top" }}>
                        {row.suggestedScore}
                      </td>
                      <td style={{ verticalAlign: "top" }}>
                        {row.kind === "factor-refresh" && (
                          <div
                            style={{
                              fontStyle: "italic",
                              marginBottom: "4px",
                            }}
                          >
                            Update tooltip to include score factors:
                          </div>
                        )}
                        <ul
                          style={{
                            margin: 0,
                            paddingLeft: "16px",
                          }}
                        >
                          {row.factors.map((f) => (
                            <li key={f.key}>
                              <span
                                style={{
                                  color: STATUS_COLOR[f.status],
                                  fontWeight: 600,
                                  marginRight: "6px",
                                }}
                              >
                                {STATUS_SYMBOL[f.status]}
                              </span>
                              <strong>{f.label}:</strong> {f.message}
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <div
              style={{
                marginTop: "16px",
                display: "flex",
                flexDirection: "row",
                width: "100%",
                justifyContent: "space-between",
                gap: "10px",
              }}
            >
              <Button variant="inrangesecondary" onClick={onClose}>
                Cancel
              </Button>
              <div style={{ display: "flex", gap: "10px" }}>
                <Button
                  variant="success"
                  disabled={checkedCount === 0}
                  onClick={() => submitUpdates(Array.from(checkedSiteIds))}
                >
                  Update score for {checkedCount} site
                  {checkedCount === 1 ? "" : "s"}
                </Button>
                <Button
                  variant="success"
                  onClick={() => submitUpdates(rows.map((r) => r.siteId))}
                >
                  Update all scores
                </Button>
              </div>
            </div>
          </>
        )}
      </ModalView>
    </Modal>
  );
};

export default BulkAutomateScoring;
